import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { claimAppSumoLicense } from '../services/appStateService';
import { appUrlForRegion, regionDisplayName, regionFromRuntime } from '../lib/regionRuntime';

function cleanRegion(value = '') {
  const clean = String(value || '').trim().toLowerCase();
  if (clean === 'eu') return 'eu';
  if (clean === 'global' || clean === 'us') return 'global';
  return '';
}

export function AppSumoActivationPage() {
  const { state, refreshAppState } = useApp();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const claimingRef = useRef(false);

  const token = String(params.get('token') || '').trim();
  const requestedRegion = cleanRegion(params.get('region') || '');
  const callbackError = String(params.get('error') || '').trim();
  const runtimeRegion = regionFromRuntime('global');
  const [status, setStatus] = useState('');
  const [error, setError] = useState(callbackError || '');

  const returnTo = useMemo(
    () => `${location.pathname}${location.search || ''}`,
    [location.pathname, location.search],
  );

  const chooseRegion = (region) => {
    const clean = cleanRegion(region);
    if (!clean || !token) return;

    const targetOrigin = appUrlForRegion(clean);
    const targetPath = `/appsumo/activate?token=${encodeURIComponent(token)}&region=${encodeURIComponent(clean)}`;

    if (window.location.origin === targetOrigin) {
      navigate(targetPath, { replace: true });
      return;
    }

    window.location.assign(`${targetOrigin}${targetPath}`);
  };

  useEffect(() => {
    if (!requestedRegion) return;
    if (runtimeRegion === requestedRegion) return;
    window.location.replace(`${appUrlForRegion(requestedRegion)}${returnTo}`);
  }, [requestedRegion, runtimeRegion, returnTo]);

  useEffect(() => {
    if (
      !token ||
      !requestedRegion ||
      runtimeRegion !== requestedRegion ||
      !state.authReady ||
      !state.user ||
      !state.initialAppLoaded ||
      claimingRef.current
    ) return;

    claimingRef.current = true;
    setStatus('claiming');
    setError('');

    (async () => {
      try {
        await claimAppSumoLicense({ token });
        await refreshAppState().catch(() => null);
        setStatus('success');

        window.setTimeout(() => {
          navigate(state.onboardingComplete ? '/home' : '/setup', { replace: true });
        }, 900);
      } catch (err) {
        claimingRef.current = false;
        setStatus('');
        setError(err?.message || 'Could not activate this AppSumo license.');
      }
    })();
  }, [
    token,
    requestedRegion,
    runtimeRegion,
    state.authReady,
    state.user,
    state.initialAppLoaded,
    state.onboardingComplete,
    refreshAppState,
    navigate,
  ]);

  if (callbackError) {
    return (
      <Box className="app-grid" minHeight="100vh">
        <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
          <Alert severity="error">AppSumo activation could not be started: {callbackError}</Alert>
        </Container>
      </Box>
    );
  }

  if (!token) {
    return (
      <Box className="app-grid" minHeight="100vh">
        <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
          <Alert severity="error">This AppSumo activation link is missing its activation token.</Alert>
        </Container>
      </Box>
    );
  }

  if (!requestedRegion) {
    return (
      <Box className="app-grid" minHeight="100vh">
        <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
          <Typography variant="h1" sx={{ fontSize: { xs: 38, md: 50 } }}>Activate your AppSumo license</Typography>
          <Typography color="text.secondary" sx={{ mt: 1.5, mb: 3 }}>
            Choose where NudgeForMe should store your account data. Your license will stay associated with that region.
          </Typography>

          <Stack spacing={1.5}>
            <Button variant="outlined" size="large" startIcon={<PublicRoundedIcon />} onClick={() => chooseRegion('global')} sx={{ justifyContent: 'flex-start', p: 2 }}>
              Global / US environment
            </Button>
            <Button variant="outlined" size="large" startIcon={<ShieldOutlinedIcon />} onClick={() => chooseRegion('eu')} sx={{ justifyContent: 'flex-start', p: 2 }}>
              Europe / EU environment
            </Button>
          </Stack>

          <Alert severity="info" sx={{ mt: 3 }}>
            Existing Snoooz or NudgeForMe users should choose the same region as their existing account.
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!state.authReady || (state.user && !state.initialAppLoaded)) {
    return (
      <Box minHeight="100vh" display="grid" sx={{ placeItems: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">Loading {regionDisplayName(requestedRegion)} environment…</Typography>
        </Stack>
      </Box>
    );
  }

  if (!state.user) {
    return (
      <Box className="app-grid" minHeight="100vh">
        <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 46 } }}>Almost there</Typography>
              <Typography color="text.secondary" sx={{ mt: 1.5 }}>
                Your AppSumo license will be activated in the <strong>{regionDisplayName(requestedRegion)}</strong> environment. Sign in or create your account to continue.
              </Typography>
              <Button variant="contained" size="large" onClick={() => navigate(`/signin?returnTo=${encodeURIComponent(returnTo)}`)} sx={{ mt: 3 }}>
                Sign in to activate
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <Box className="app-grid" minHeight="100vh">
        <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
          <Card>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleRoundedIcon color="success" sx={{ fontSize: 56 }} />
              <Typography variant="h1" sx={{ fontSize: 38, mt: 2 }}>AppSumo activated</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>Your lifetime plan is attached to this NudgeForMe account.</Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="app-grid" minHeight="100vh">
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 46 } }}>Activating AppSumo</Typography>
            <Typography color="text.secondary" sx={{ mt: 1.5 }}>Applying your license to the {regionDisplayName(requestedRegion)} environment.</Typography>
            {status === 'claiming' && (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
                <CircularProgress size={22} />
                <Typography>Verifying license…</Typography>
              </Stack>
            )}
            {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
            {error && <Button variant="outlined" onClick={() => window.location.reload()} sx={{ mt: 2 }}>Try again</Button>}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
